import { getValidatedDurationS } from "@/utils/getAudioLength/getValidatedDurationS";
import { getMp3DurationFromFrames } from "@/utils/getAudioLength/mp3/getMp3DurationFromFrames";

const MP3_CONTAINER = "MPEG";
// music-metadata names Matroska/WebM containers "EBML/…"
const WEBM_CONTAINER_PREFIX = "EBML";
// FLAC's STREAMINFO and AAC's MP4 movie header state a total nothing else in the file corroborates, and their variable bitrate defeats the size check, so their duration always comes from decoding (Chrome's decoder agrees with the AI backend on both). Keyed on music-metadata's codec string rather than the container: AAC arrives under several MP4 brands, and ALAC shares the M4A container but Chrome cannot decode it, so demoting ALAC would leave the upload with no measurement.
const HEADER_UNVERIFIABLE_CODECS = ["FLAC", "MPEG-4/AAC"];

// Measures duration by parsing the container/frame headers instead of decoding audio. Chrome's decodeAudioData silently drops the silent frames some MP3 encoders emit, under-reporting length by several seconds and tripping the AI backend's length check; frame parsing counts them the same way the backend's decoder does.
export const getAudioLengthFromMetadata = async (
  audioFile: File
): Promise<number | null> => {
  // Loaded on demand so the parser only ships to the browser when someone actually picks a file
  const { parseWebStream } = await import("music-metadata");

  try {
    // Chrome 155 can leave native BYOB file reads pending while the waveform preview reads the same WAV. A transform exposes only ordinary reads to the parser while preserving bounded streaming and cancellation.
    const audioStream = audioFile
      .stream()
      .pipeThrough(new TransformStream<Uint8Array, Uint8Array>());
    // Omit name and MIME hints so mislabeled files are identified from their bytes; leave the MP3 full-scan option off because its frames are counted below instead.
    const { format } = await parseWebStream(audioStream, {
      size: audioFile.size,
    });

    // A WebM timeline is wall-clock time from MediaRecorder, which keeps running while a mic delivers nothing, so it can overstate the audio by a minute and nothing in the container contradicts it; only decoding counts what is there
    const isWebm = format.container?.startsWith(WEBM_CONTAINER_PREFIX) === true;
    if (
      isWebm ||
      (format.codec !== undefined &&
        HEADER_UNVERIFIABLE_CODECS.includes(format.codec))
    ) {
      return null;
    }

    // music-metadata's MP3 duration is a header summary (a Xing frame count, or a CBR estimate from the first three frames) that truncated, joined, or VBR-without-Xing files contradict; counting the frames measures what decoders will produce
    if (format.container === MP3_CONTAINER) {
      const frameDurationS = await getMp3DurationFromFrames(audioFile);
      return frameDurationS === null ? null : Math.floor(frameDurationS);
    }

    // null (missing or implausible duration) demotes to the decode fallbacks in getAudioLengthClient, which measure the frames actually present
    return getValidatedDurationS(format, audioFile.size);
  } catch (error) {
    // Files music-metadata cannot measure are an expected outcome for arbitrary user uploads, not a parser failure worth telemetry — normalize to null so the caller falls back silently. CouldNotDetermineFileTypeError = no recognizable signature; UnsupportedFileTypeError = recognized container with no parser (e.g. a 3gpp phone recording). Matched by name because music-metadata v10 does not export the error classes from its public entry.
    const isUnmeasurableFormat =
      error instanceof Error &&
      (error.name === "CouldNotDetermineFileTypeError" ||
        error.name === "UnsupportedFileTypeError");

    if (isUnmeasurableFormat) {
      return null;
    }

    throw error;
  }
};
